import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TrackingControl } from '../../components/tracking/TrackingControl';
import { 
  Car, MapPin, Clock, DollarSign, Download, CheckCircle, 
  Circle, Plus, Trash2, Star, Loader2, Calendar, FileText,
  ChevronDown, ChevronUp, Navigation, Route, MapPinned,
  Briefcase, Coffee, ArrowRight, CheckSquare, AlertCircle,
  Gauge, PlusCircle, Edit3, MoreVertical
} from 'lucide-react';

const IRS_RATE = 0.725;

// Custom markers for Leaflet - matching Routes page style (text-only with shadow)
const createNumberedIcon = (label, color = '#ffffff') => {
  return L.divIcon({
    className: 'custom-numbered-marker',
    html: `<div style="color: ${color}; font-weight: bold; font-size: 14px; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000, 0 0 6px #000;">${label}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Start/End marker icons
const createStartEndIcon = (isStart) => {
  const color = isStart ? '#4ade80' : '#f87171';
  const letter = isStart ? 'S' : 'E';
  return L.divIcon({
    className: 'custom-start-end-marker',
    html: `<div style="color: ${color}; font-weight: bold; font-size: 15px; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000, 0 0 6px #000;">${letter}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

const startIcon = createStartEndIcon(true);
const endIcon = createStartEndIcon(false);
const stopIcon = (num) => createNumberedIcon(num, '#14b8a6'); // teal color to match app theme
const highlightIcon = (num) => createNumberedIcon(num, '#f59e0b'); // amber when highlighted

// Map bounds fitter component
function MapFitter({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [bounds, map]);
  return null;
}

// Interactive Mileage Map component
function MileageMap({ entry, polyline, highlightedSegmentIdx, onSegmentClick, onFixMap, isFixingMap }) {
  const points = [];
  
  if (entry.start_lat && entry.start_lng) {
    points.push({ lat: entry.start_lat, lng: entry.start_lng, type: 'start', label: 'S' });
  }
  
  if (entry.segments && entry.segments.length > 0) {
    entry.segments.forEach((seg, idx) => {
      if (seg.to_lat && seg.to_lng) {
        points.push({ 
          lat: seg.to_lat, 
          lng: seg.to_lng, 
          type: idx === entry.segments.length - 1 ? 'end' : 'stop',
          label: idx === entry.segments.length - 1 ? 'E' : (idx + 1).toString(),
          segmentIdx: idx,
          defendant: seg.to_defendant,
          address: seg.to_address,
          miles: seg.miles
        });
      }
    });
  }
  
  if (points.length < 2) {
    return (
      <div className="h-48 bg-slate-100 dark:bg-slate-800 rounded-lg flex flex-col items-center justify-center gap-3">
        <MapPinned className="h-8 w-8 text-slate-400" />
        <p className="text-slate-500">Not enough location data to display map</p>
        {onFixMap && (
          <Button
            size="sm"
            variant="outline"
            onClick={onFixMap}
            disabled={isFixingMap}
          >
            {isFixingMap ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Geocoding...</>
            ) : (
              <><MapPin className="h-4 w-4 mr-2" /> Fix Map (Geocode Addresses)</>
            )}
          </Button>
        )}
      </div>
    );
  }
  
  const bounds = points.map(p => [p.lat, p.lng]);
  
  // Build polyline positions
  const polylinePositions = polyline?.coordinates 
    ? polyline.coordinates.map(c => [c[1], c[0]]) // GeoJSON is [lng, lat], Leaflet needs [lat, lng]
    : points.map(p => [p.lat, p.lng]);
  
  return (
    <MapContainer
      style={{ height: '220px', width: '100%', borderRadius: '8px', zIndex: 0, position: 'relative' }}
      center={[points[0].lat, points[0].lng]}
      zoom={13}
      scrollWheelZoom={false}
    >
      {/* Dark themed map tiles */}
      <TileLayer
        attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/512/{z}/{x}/{y}@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`}
        tileSize={512}
        zoomOffset={-1}
      />
      <MapFitter bounds={bounds} />
      
      {/* Route polyline */}
      <Polyline 
        positions={polylinePositions} 
        color="#14b8a6" 
        weight={4} 
        opacity={0.8}
      />
      
      {/* Markers */}
      {points.map((point, idx) => {
        let icon;
        if (point.type === 'start') {
          icon = startIcon;
        } else if (point.type === 'end') {
          icon = endIcon;
        } else if (highlightedSegmentIdx === point.segmentIdx) {
          icon = highlightIcon(point.label);
        } else {
          icon = stopIcon(point.label);
        }
        
        return (
          <Marker
            key={idx}
            position={[point.lat, point.lng]}
            icon={icon}
            eventHandlers={{
              click: () => {
                if (point.segmentIdx !== undefined) {
                  onSegmentClick(point.segmentIdx);
                }
              }
            }}
          >
            <Popup>
              <div className="text-sm">
                <strong>{point.type === 'start' ? 'Start' : point.defendant || `Stop ${point.label}`}</strong>
                {point.address && <p className="text-xs text-slate-500">{point.address}</p>}
                {point.miles && <p className="text-xs text-teal-600">{point.miles.toFixed(2)} mi to here</p>}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
}

export default function MileageLogTab() {
  const [loading, setLoading] = useState(true);
  const [mileageLog, setMileageLog] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [defaultVehicleId, setDefaultVehicleId] = useState(null);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState({});
  const [selectedSegments, setSelectedSegments] = useState({});
  const [updatingSegments, setUpdatingSegments] = useState(false);
  const [highlightedSegment, setHighlightedSegment] = useState({});
  const [entryPolylines, setEntryPolylines] = useState({});
  const [loadingPolylines, setLoadingPolylines] = useState({});
  const [fixingMap, setFixingMap] = useState({});  // {entryId: true/false}
  
  // Vehicle dialog
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({ name: '', make: '', model: '', year: '', initial_odometer: '' });
  const [savingVehicle, setSavingVehicle] = useState(false);
  
  // Manual entry dialog
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    from_address: '', to_address: '', miles: '',
    is_business: null, vehicle_id: null, notes: ''
  });
  const [creatingManualEntry, setCreatingManualEntry] = useState(false);
  
  // Entry vehicle assignment
  const [assigningVehicle, setAssigningVehicle] = useState(null);

  const toggleExpanded = async (entryId) => {
    const wasExpanded = expandedEntries[entryId];
    setExpandedEntries(prev => ({ ...prev, [entryId]: !prev[entryId] }));
    
    if (!wasExpanded && !entryPolylines[entryId] && !loadingPolylines[entryId]) {
      setLoadingPolylines(prev => ({ ...prev, [entryId]: true }));
      try {
        const response = await api.getEntryPolyline(entryId);
        if (response.data.polyline) {
          setEntryPolylines(prev => ({ ...prev, [entryId]: response.data.polyline }));
        }
      } catch (error) {
        console.error('Error fetching polyline:', error);
      } finally {
        setLoadingPolylines(prev => ({ ...prev, [entryId]: false }));
      }
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [logRes, vehiclesRes] = await Promise.all([
        api.getMileageLog(filterYear, filterMonth),
        api.getVehicles()
      ]);
      setMileageLog(logRes.data);
      setVehicles(vehiclesRes.data.vehicles || []);
      setDefaultVehicleId(logRes.data.default_vehicle_id);
    } catch (error) {
      toast.error('Failed to load mileage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filterYear, filterMonth]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      const date = new Date(dateStr + 'T12:00:00');
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Segment handlers
  const handleSegmentClick = (entryId, segmentIdx) => {
    setHighlightedSegment(prev => ({
      ...prev,
      [entryId]: prev[entryId] === segmentIdx ? null : segmentIdx
    }));
  };

  const toggleSegmentSelection = (entryId, segmentId) => {
    setSelectedSegments(prev => {
      const entrySelections = new Set(prev[entryId] || []);
      if (entrySelections.has(segmentId)) {
        entrySelections.delete(segmentId);
      } else {
        entrySelections.add(segmentId);
      }
      return { ...prev, [entryId]: entrySelections };
    });
  };

  const selectAllSegments = (entryId, segments) => {
    setSelectedSegments(prev => ({
      ...prev,
      [entryId]: new Set(segments.map(s => s.id))
    }));
  };

  const clearSegmentSelection = (entryId) => {
    setSelectedSegments(prev => ({ ...prev, [entryId]: new Set() }));
  };

  const handleSegmentClassificationChange = async (entryId, segmentId, isBusiness) => {
    try {
      await api.updateSegmentClassification(entryId, segmentId, isBusiness);
      const label = isBusiness === true ? 'Business' : isBusiness === false ? 'Personal' : 'Unmarked';
      toast.success(`Marked as ${label}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update segment');
    }
  };

  const handleBulkSegmentUpdate = async (entryId, isBusiness) => {
    const selected = selectedSegments[entryId];
    if (!selected || selected.size === 0) {
      toast.error('No segments selected');
      return;
    }
    setUpdatingSegments(true);
    try {
      await api.bulkUpdateSegments(entryId, Array.from(selected), isBusiness);
      const label = isBusiness === true ? 'Business' : 'Personal';
      toast.success(`${selected.size} segment(s) marked as ${label}`);
      clearSegmentSelection(entryId);
      loadData();
    } catch (error) {
      toast.error('Failed to update segments');
    } finally {
      setUpdatingSegments(false);
    }
  };

  // Fix map handler (geocode addresses to get coordinates)
  const handleFixMap = async (entryId) => {
    setFixingMap(prev => ({ ...prev, [entryId]: true }));
    try {
      await api.geocodeMileageEntry(entryId);
      toast.success('Map data updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to geocode addresses');
    } finally {
      setFixingMap(prev => ({ ...prev, [entryId]: false }));
    }
  };

  // Vehicle handlers
  const openVehicleDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      // When editing, load current_odometer (or fall back to initial_odometer)
      const odometerValue = vehicle.current_odometer ?? vehicle.initial_odometer;
      setVehicleForm({
        name: vehicle.name || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year?.toString() || '',
        initial_odometer: odometerValue?.toString() || ''
      });
    } else {
      setEditingVehicle(null);
      setVehicleForm({ name: '', make: '', model: '', year: '', initial_odometer: '' });
    }
    setShowVehicleDialog(true);
  };

  const handleSaveVehicle = async () => {
    if (!vehicleForm.name) {
      toast.error('Vehicle name is required');
      return;
    }
    setSavingVehicle(true);
    try {
      if (editingVehicle) {
        // When editing, update current_odometer (not initial_odometer)
        // initial_odometer should remain as the original starting point
        const odometerValue = vehicleForm.initial_odometer ? parseFloat(vehicleForm.initial_odometer) : null;
        await api.updateVehicle(editingVehicle.id, {
          name: vehicleForm.name,
          make: vehicleForm.make || null,
          model: vehicleForm.model || null,
          year: vehicleForm.year ? parseInt(vehicleForm.year) : null,
          current_odometer: odometerValue
        });
        toast.success('Vehicle updated');
      } else {
        const res = await api.createVehicle({
          name: vehicleForm.name,
          make: vehicleForm.make,
          model: vehicleForm.model,
          year: vehicleForm.year ? parseInt(vehicleForm.year) : null
        });
        if (vehicleForm.initial_odometer && res.data.id) {
          await api.updateVehicleOdometer(res.data.id, parseFloat(vehicleForm.initial_odometer));
        }
        toast.success('Vehicle added');
      }
      setShowVehicleDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save vehicle');
    } finally {
      setSavingVehicle(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Delete this vehicle? Mileage entries will keep their data but vehicle assignment will be removed.')) return;
    try {
      await api.deleteVehicle(vehicleId);
      toast.success('Vehicle deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  const handleSetDefaultVehicle = async (vehicleId) => {
    try {
      await api.setDefaultVehicle(vehicleId);
      toast.success('Default vehicle updated');
      loadData();
    } catch (error) {
      toast.error('Failed to set default vehicle');
    }
  };

  const handleAssignVehicleToEntry = async (entryId, vehicleId) => {
    try {
      await api.updateMileageEntryVehicle(entryId, vehicleId);
      toast.success('Vehicle assigned');
      setAssigningVehicle(null);
      loadData();
    } catch (error) {
      toast.error('Failed to assign vehicle');
    }
  };

  // Manual entry handlers
  const handleCreateManualEntry = async () => {
    if (!manualEntry.from_address || !manualEntry.to_address) {
      toast.error('From and To addresses are required');
      return;
    }
    setCreatingManualEntry(true);
    try {
      const res = await api.createManualMileageEntry({
        date: manualEntry.date,
        from_address: manualEntry.from_address,
        to_address: manualEntry.to_address,
        miles: manualEntry.miles ? parseFloat(manualEntry.miles) : null,
        is_business: manualEntry.is_business,
        vehicle_id: manualEntry.vehicle_id,
        notes: manualEntry.notes || null
      });
      toast.success(`Manual entry added: ${res.data.miles} miles`);
      setShowManualEntryDialog(false);
      setManualEntry({
        date: new Date().toISOString().split('T')[0],
        from_address: '', to_address: '', miles: '',
        is_business: null, vehicle_id: null, notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    } finally {
      setCreatingManualEntry(false);
    }
  };

  const handleMarkReviewed = async (entryId, currentStatus) => {
    try {
      await api.markMileageReviewed(entryId, !currentStatus);
      toast.success(currentStatus ? 'Marked as not reviewed' : 'Marked as reviewed');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.exportMileageLog(filterYear, filterMonth);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mileage_log_${filterYear}${filterMonth ? `_${filterMonth}` : ''}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, []);

  const months = [
    { value: null, label: 'All Months' },
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const summary = mileageLog?.summary || {};

  return (
    <div className="space-y-6 p-4" data-testid="mileage-log-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">Mileage Log</h2>
          <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Track business & personal miles for tax deductions</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowManualEntryDialog(true)} data-testid="add-manual-entry-btn" className="text-xs md:text-sm">
            <PlusCircle className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Add Trip</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => openVehicleDialog()} data-testid="add-vehicle-btn" className="text-xs md:text-sm">
            <Car className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Vehicles</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="export-csv-btn" className="text-xs md:text-sm">
            {exporting ? <Loader2 className="h-4 w-4 md:mr-2 animate-spin" /> : <Download className="h-4 w-4 md:mr-2" />}
            <span className="hidden md:inline">Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 md:gap-4">
        <Select value={filterYear.toString()} onValueChange={(v) => setFilterYear(parseInt(v))}>
          <SelectTrigger className="w-24 md:w-32" data-testid="year-filter">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {years.map(year => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterMonth?.toString() || 'all'} onValueChange={(v) => setFilterMonth(v === 'all' ? null : parseInt(v))}>
          <SelectTrigger className="w-28 md:w-40" data-testid="month-filter">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {months.map(m => (
              <SelectItem key={m.value || 'all'} value={m.value?.toString() || 'all'}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Auto-Tracking Control (Only visible on native mobile app) */}
      <TrackingControl />

      {/* Summary Card - New layout: Row 1: Trips, Stops, Deduction | Row 2: Business, Personal, Total */}
      {mileageLog?.summary && (
        <Card className="bg-gradient-to-r from-teal-500/10 to-green-500/10 border-teal-500/20">
          <CardContent className="p-3 md:p-4">
            <div className="grid grid-cols-3 gap-2 md:gap-4 text-center">
              {/* Row 1: Trips, Stops, Deduction */}
              <div>
                <p className="text-lg md:text-2xl font-bold text-slate-900 dark:text-white">{summary.total_trips}</p>
                <p className="text-[10px] md:text-sm text-slate-500 dark:text-slate-400">Trips</p>
              </div>
              <div>
                <p className="text-lg md:text-2xl font-bold text-slate-900 dark:text-white">{summary.total_stops || 0}</p>
                <p className="text-[10px] md:text-sm text-slate-500 dark:text-slate-400">Stops</p>
              </div>
              <div>
                <p className="text-lg md:text-2xl font-bold text-green-600">${summary.total_deduction?.toFixed(2) || '0.00'}</p>
                <p className="text-[10px] md:text-sm text-slate-500 dark:text-slate-400">Deduction</p>
              </div>
              {/* Row 2: Business, Personal, Total */}
              <div>
                <p className="text-lg md:text-2xl font-bold text-green-600">{summary.business_miles?.toFixed(2) || '0.00'}</p>
                <p className="text-[10px] md:text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1">
                  <Briefcase className="h-3 w-3 hidden md:inline" /> Business
                </p>
              </div>
              <div>
                <p className="text-lg md:text-2xl font-bold text-amber-500">{summary.personal_miles?.toFixed(2) || '0.00'}</p>
                <p className="text-[10px] md:text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1">
                  <Coffee className="h-3 w-3 hidden md:inline" /> Personal
                </p>
              </div>
              <div>
                <p className="text-lg md:text-2xl font-bold text-slate-700 dark:text-slate-300">{summary.total_miles?.toFixed(2) || '0.00'}</p>
                <p className="text-[10px] md:text-sm text-slate-500 dark:text-slate-400">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mileage Entries */}
      {mileageLog?.entries?.length > 0 ? (
        <div className="space-y-4">
          {Object.entries(mileageLog.grouped || {}).map(([date, entries]) => (
            <div key={date} className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {formatDate(date)}
              </h3>
              
              {entries.map((entry) => {
                const isExpanded = expandedEntries[entry.id];
                const polyline = entryPolylines[entry.id];
                const highlightIdx = highlightedSegment[entry.id];
                const hasUnmarked = entry.unmarked_miles > 0;
                const entryVehicle = vehicles.find(v => v.id === entry.vehicle_id);
                
                return (
                  <Card key={entry.id} className={`overflow-hidden transition-all ${entry.reviewed ? 'border-green-300 dark:border-green-700' : ''}`}>
                    <CardContent className="p-0">
                      {/* Card Header */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                        onClick={() => toggleExpanded(entry.id)}
                        data-testid={`mileage-entry-${entry.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <h4 className="font-semibold text-slate-900 dark:text-white truncate text-sm md:text-base">
                                {entry.route_name}
                              </h4>
                              {entry.is_manual && (
                                <Badge variant="outline" className="text-[10px] md:text-xs">Manual</Badge>
                              )}
                              {entry.is_auto_tracked && (
                                <Badge variant="outline" className="text-[10px] md:text-xs border-blue-500 text-blue-600">
                                  <Navigation className="h-3 w-3 mr-0.5" />GPS
                                </Badge>
                              )}
                              {entry.reviewed && (
                                <Badge className="bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 text-[10px] md:text-xs">
                                  <CheckCircle className="h-3 w-3 mr-0.5 md:mr-1" />
                                  <span className="hidden md:inline">Reviewed</span>
                                </Badge>
                              )}
                              {hasUnmarked && (
                                <Badge variant="outline" className="text-[10px] md:text-xs border-amber-500 text-amber-600">
                                  <AlertCircle className="h-3 w-3 mr-0.5 md:mr-1" />
                                  {entry.unmarked_miles.toFixed(2)} mi
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm text-slate-500 dark:text-slate-400 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Route className="h-3 w-3" />
                                {entry.segments?.length || 0} stops
                              </span>
                              <button
                                className="flex items-center gap-1 hover:text-teal-600 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAssigningVehicle(assigningVehicle === entry.id ? null : entry.id);
                                }}
                              >
                                <Car className="h-3 w-3" />
                                {entryVehicle ? (
                                  <>
                                    <span className="hidden md:inline">{entryVehicle.name}</span>
                                    <span className="md:hidden">{entryVehicle.name.split(' ')[0]}</span>
                                    {entry.vehicle_id === defaultVehicleId && (
                                      <span className="hidden md:inline text-xs text-teal-500">(Default)</span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-slate-400">No vehicle</span>
                                )}
                                <Edit3 className="h-3 w-3" />
                              </button>
                              <Badge variant="outline" className="text-[10px] md:text-xs">
                                {entry.miles_source === 'gps' ? 'GPS' : 'Manual'}
                              </Badge>
                            </div>
                            
                            {/* Vehicle assignment dropdown */}
                            {assigningVehicle === entry.id && (
                              <div className="mt-2 p-2 bg-slate-100 dark:bg-slate-800 rounded-lg" onClick={(e) => e.stopPropagation()}>
                                <Label className="text-xs mb-1 block">Assign Vehicle</Label>
                                <Select
                                  value={entry.vehicle_id || 'none'}
                                  onValueChange={(v) => handleAssignVehicleToEntry(entry.id, v === 'none' ? null : v)}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue placeholder="Select vehicle" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No vehicle</SelectItem>
                                    {vehicles.map(v => (
                                      <SelectItem key={v.id} value={v.id}>
                                        {v.name} {v.id === defaultVehicleId && '(Default)'}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-right flex-shrink-0 flex items-center gap-2 md:gap-4">
                            <div>
                              {entry.business_miles > 0 && (
                                <p className="text-base md:text-lg font-bold text-green-600">{entry.business_miles.toFixed(2)} mi</p>
                              )}
                              {entry.personal_miles > 0 && (
                                <p className="text-[10px] md:text-xs text-amber-500">+{entry.personal_miles.toFixed(2)} personal</p>
                              )}
                              <p className="text-xs md:text-sm font-medium text-green-600">${entry.deduction?.toFixed(2)}</p>
                            </div>
                            <ChevronDown className={`h-4 w-4 md:h-5 md:w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded Section */}
                      {isExpanded && (
                        <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
                          {/* Leaflet Map View */}
                          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                <MapPinned className="h-4 w-4 text-teal-500" />
                                Route Map
                                {loadingPolylines[entry.id] && (
                                  <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                                )}
                              </h5>
                              {highlightIdx !== null && highlightIdx !== undefined && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs h-6"
                                  onClick={() => setHighlightedSegment(prev => ({ ...prev, [entry.id]: null }))}
                                >
                                  Clear highlight
                                </Button>
                              )}
                            </div>
                            <MileageMap
                              entry={entry}
                              polyline={polyline}
                              highlightedSegmentIdx={highlightIdx}
                              onSegmentClick={(idx) => handleSegmentClick(entry.id, idx)}
                              onFixMap={() => handleFixMap(entry.id)}
                              isFixingMap={fixingMap[entry.id]}
                            />
                            <p className="text-xs text-slate-400 mt-1">Click markers or segments below to highlight on map</p>
                          </div>
                          
                          {/* Segments List */}
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                <Navigation className="h-4 w-4 text-teal-500" />
                                Trip Segments ({entry.segments?.length || 0})
                              </h5>
                              
                              {entry.segments && entry.segments.length > 0 && (
                                <div className="flex items-center gap-2">
                                  {selectedSegments[entry.id]?.size > 0 && (
                                    <>
                                      <span className="text-xs text-slate-500">{selectedSegments[entry.id].size} selected</span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs border-green-500 text-green-600 hover:bg-green-50"
                                        onClick={(e) => { e.stopPropagation(); handleBulkSegmentUpdate(entry.id, true); }}
                                        disabled={updatingSegments}
                                      >
                                        <Briefcase className="h-3 w-3 mr-1" />
                                        Business
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs border-amber-500 text-amber-600 hover:bg-amber-50"
                                        onClick={(e) => { e.stopPropagation(); handleBulkSegmentUpdate(entry.id, false); }}
                                        disabled={updatingSegments}
                                      >
                                        <Coffee className="h-3 w-3 mr-1" />
                                        Personal
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-xs"
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      if (selectedSegments[entry.id]?.size === entry.segments.length) {
                                        clearSegmentSelection(entry.id);
                                      } else {
                                        selectAllSegments(entry.id, entry.segments);
                                      }
                                    }}
                                  >
                                    <CheckSquare className="h-3 w-3 mr-1" />
                                    {selectedSegments[entry.id]?.size === entry.segments?.length ? 'Deselect' : 'Select All'}
                                  </Button>
                                </div>
                              )}
                            </div>
                            
                            {entry.segments && entry.segments.length > 0 ? (
                              <div className="space-y-3">
                                {entry.segments.map((segment, idx) => {
                                  const isSelected = selectedSegments[entry.id]?.has(segment.id);
                                  const isBusiness = segment.is_business === true;
                                  const isPersonal = segment.is_business === false;
                                  const isUnmarked = segment.is_business === null || segment.is_business === undefined;
                                  const isHighlighted = highlightIdx === idx;
                                  
                                  return (
                                    <div 
                                      key={segment.id || idx} 
                                      className={`p-3 rounded-lg border transition-all cursor-pointer ${
                                        isHighlighted 
                                          ? 'ring-2 ring-amber-400 border-amber-300 bg-amber-50/50'
                                          : isUnmarked 
                                          ? 'bg-slate-100/50 dark:bg-slate-700/30 border-slate-300 dark:border-slate-600' 
                                          : isBusiness 
                                          ? 'bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800' 
                                          : 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                                      }`}
                                      onClick={() => handleSegmentClick(entry.id, idx)}
                                    >
                                      <div className="flex items-start gap-2 md:gap-3">
                                        <Checkbox
                                          checked={isSelected}
                                          onCheckedChange={() => toggleSegmentSelection(entry.id, segment.id)}
                                          onClick={(e) => e.stopPropagation()}
                                          className="mt-0.5 data-[state=checked]:bg-teal-500 h-4 w-4"
                                        />
                                        
                                        {/* Text-based segment number matching map style */}
                                        <div 
                                          className="flex-shrink-0 w-4 md:w-5 h-4 md:h-5 flex items-center justify-center text-xs md:text-sm font-bold"
                                          style={{
                                            color: isHighlighted ? '#f59e0b' : '#14b8a6',
                                            textShadow: '-1px -1px 0 rgba(0,0,0,0.3), 1px -1px 0 rgba(0,0,0,0.3), -1px 1px 0 rgba(0,0,0,0.3), 1px 1px 0 rgba(0,0,0,0.3)'
                                          }}
                                        >
                                          {idx + 1}
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                          {/* Mobile: Stacked layout to prevent overlap */}
                                          <div className="md:hidden">
                                            <div className="flex items-center justify-between gap-2">
                                              <div className="flex-1 min-w-0 truncate text-xs">
                                                {segment.from_is_start ? (
                                                  <span className="text-green-600 font-medium">Start</span>
                                                ) : (
                                                  <span className="text-slate-600 dark:text-slate-300">{segment.from_address?.split(',')[0] || 'Unknown'}</span>
                                                )}
                                              </div>
                                              <span className="text-slate-400 flex-shrink-0">→</span>
                                              <div className="flex-1 min-w-0 truncate text-xs font-medium text-right">
                                                {segment.to_is_end ? (
                                                  <span className="text-red-500">End</span>
                                                ) : (
                                                  <span className="text-slate-800 dark:text-slate-200">{segment.to_address?.split(',')[0] || 'Unknown'}</span>
                                                )}
                                              </div>
                                            </div>
                                            <div className="flex items-center justify-between mt-0.5">
                                              {segment.to_defendant ? (
                                                <span className="text-[10px] text-teal-600 dark:text-teal-400 truncate">{segment.to_defendant}</span>
                                              ) : segment.to_is_end ? (
                                                <span className="text-[10px] text-slate-400 truncate">{segment.to_address?.split(',')[0]}</span>
                                              ) : (
                                                <span></span>
                                              )}
                                              <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex-shrink-0">
                                                {segment.miles?.toFixed(2)} mi
                                                {isBusiness && <span className="text-green-600 ml-1">${(segment.miles * IRS_RATE).toFixed(2)}</span>}
                                              </span>
                                            </div>
                                          </div>
                                          {/* Desktop: Two line layout */}
                                          <div className="hidden md:block">
                                            <div className="text-sm mb-1">
                                              <span className="text-slate-500 dark:text-slate-400">From:</span>{' '}
                                              {segment.from_is_start ? (
                                                <span className="text-green-600 font-medium">
                                                  Start ({segment.from_address?.split(',')[0] || 'Unknown'})
                                                </span>
                                              ) : (
                                                <span className="text-slate-700 dark:text-slate-300">
                                                  {segment.from_address?.split(',')[0] || 'Unknown'}
                                                  {segment.from_defendant && (
                                                    <span className="text-teal-600 dark:text-teal-400"> ({segment.from_defendant})</span>
                                                  )}
                                                </span>
                                              )}
                                            </div>
                                            <div className="text-sm">
                                              <span className="text-slate-500 dark:text-slate-400">To:</span>{' '}
                                              {segment.to_is_end ? (
                                                <span className="text-red-500 font-medium">
                                                  End ({segment.to_address?.split(',')[0] || 'Unknown'})
                                                </span>
                                              ) : (
                                                <span className="font-medium text-slate-800 dark:text-slate-200">
                                                  {segment.to_address?.split(',')[0] || 'Unknown'}
                                                  {segment.to_defendant && (
                                                    <span className="text-teal-600 dark:text-teal-400"> ({segment.to_defendant})</span>
                                                  )}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Desktop only: Mileage column */}
                                        <div className="hidden md:block text-right flex-shrink-0">
                                          <p className="font-semibold text-sm text-slate-800 dark:text-slate-200">
                                            {segment.miles?.toFixed(2) || '0.0'} mi
                                          </p>
                                          {isBusiness && (
                                            <p className="text-xs text-green-600">${(segment.miles * IRS_RATE).toFixed(2)}</p>
                                          )}
                                        </div>
                                        
                                        {/* Mobile: Compact buttons */}
                                        <div className="flex flex-col gap-1 md:hidden flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                          <button
                                            onClick={() => handleSegmentClassificationChange(entry.id, segment.id, true)}
                                            className={`p-1.5 rounded text-[10px] font-medium transition-colors ${
                                              isBusiness
                                                ? 'bg-green-500 text-white'
                                                : 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300'
                                            }`}
                                          >
                                            <Briefcase className="h-3 w-3" />
                                          </button>
                                          <button
                                            onClick={() => handleSegmentClassificationChange(entry.id, segment.id, false)}
                                            className={`p-1.5 rounded text-[10px] font-medium transition-colors ${
                                              isPersonal
                                                ? 'bg-amber-500 text-white'
                                                : 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300'
                                            }`}
                                          >
                                            <Coffee className="h-3 w-3" />
                                          </button>
                                        </div>
                                        
                                        {/* Desktop: Full buttons */}
                                        <div className="hidden md:flex gap-1" onClick={(e) => e.stopPropagation()}>
                                          <button
                                            onClick={() => handleSegmentClassificationChange(entry.id, segment.id, true)}
                                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                              isBusiness
                                                ? 'bg-green-500 text-white'
                                                : 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 hover:bg-green-100'
                                            }`}
                                          >
                                            <Briefcase className="h-3 w-3 inline mr-1" />
                                            Business
                                          </button>
                                          <button
                                            onClick={() => handleSegmentClassificationChange(entry.id, segment.id, false)}
                                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                              isPersonal
                                                ? 'bg-amber-500 text-white'
                                                : 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 hover:bg-amber-100'
                                            }`}
                                          >
                                            <Coffee className="h-3 w-3 inline mr-1" />
                                            Personal
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                
                                {/* Summary */}
                                <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-200 dark:border-slate-700 text-sm flex-wrap gap-2">
                                  <div className="flex items-center gap-4 flex-wrap">
                                    {entry.business_miles > 0 && (
                                      <span className="flex items-center gap-1 text-green-600">
                                        <Briefcase className="h-3 w-3" />
                                        Business: {entry.business_miles.toFixed(2)} mi
                                      </span>
                                    )}
                                    {entry.personal_miles > 0 && (
                                      <span className="flex items-center gap-1 text-amber-600">
                                        <Coffee className="h-3 w-3" />
                                        Personal: {entry.personal_miles.toFixed(2)} mi
                                      </span>
                                    )}
                                    {entry.unmarked_miles > 0 && (
                                      <span className="flex items-center gap-1 text-slate-500">
                                        <AlertCircle className="h-3 w-3" />
                                        Unmarked: {entry.unmarked_miles.toFixed(2)} mi
                                      </span>
                                    )}
                                    <span className="text-slate-600 dark:text-slate-400">
                                      Total: {entry.miles.toFixed(2)} mi
                                    </span>
                                  </div>
                                  <span className="font-medium text-green-600">
                                    Deduction: ${entry.deduction?.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 dark:text-slate-400">No segments available.</p>
                            )}
                            
                            {/* Review Button */}
                            <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                              <Button
                                variant={entry.reviewed ? "outline" : "default"}
                                size="sm"
                                onClick={() => handleMarkReviewed(entry.id, entry.reviewed)}
                                className={entry.reviewed ? 'text-green-600 border-green-600' : ''}
                              >
                                {entry.reviewed ? (
                                  <><CheckCircle className="h-4 w-4 mr-2" /> Reviewed</>
                                ) : (
                                  <><Circle className="h-4 w-4 mr-2" /> Mark as Reviewed</>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Route className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">No mileage data</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Complete routes or add manual entries to track your mileage
            </p>
            <Button onClick={() => setShowManualEntryDialog(true)}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Manual Trip
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Vehicle Dialog */}
      <Dialog open={showVehicleDialog} onOpenChange={setShowVehicleDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Manage Vehicles'}</DialogTitle>
            <DialogDescription>
              {editingVehicle ? 'Update vehicle details' : 'Add and manage your vehicles. Set one as default for new trips.'}
            </DialogDescription>
          </DialogHeader>
          
          {!editingVehicle && vehicles.length > 0 && (
            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
              <Label className="text-sm font-medium">Your Vehicles</Label>
              {vehicles.map(v => (
                <div key={v.id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                  <div className="flex-1">
                    <span className="font-medium">{v.name}</span>
                    {v.make && <span className="text-sm text-slate-500 ml-2">{v.year} {v.make} {v.model}</span>}
                    {v.id === defaultVehicleId && (
                      <Badge className="ml-2 text-xs">Default</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {v.initial_odometer && (
                      <div className="text-xs text-slate-500 flex flex-col items-end mr-3">
                        <span className="flex items-center gap-1">
                          <Gauge className="h-3 w-3" />
                          <span className="text-slate-400">Start:</span> {v.initial_odometer.toLocaleString()} mi
                        </span>
                        {v.current_odometer && v.current_odometer !== v.initial_odometer && (
                          <span className="text-teal-500 font-medium">
                            Current: {v.current_odometer.toLocaleString()} mi
                          </span>
                        )}
                      </div>
                    )}
                    {v.id !== defaultVehicleId && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleSetDefaultVehicle(v.id)}>
                        <Star className="h-3 w-3 mr-1" />
                        Set Default
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7" onClick={() => openVehicleDialog(v)}>
                      <Edit3 className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-red-500 hover:text-red-600" onClick={() => handleDeleteVehicle(v.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className={!editingVehicle && vehicles.length > 0 ? "border-t pt-4" : ""}>
            <Label className="text-sm font-medium mb-2 block">{editingVehicle ? 'Vehicle Details' : 'Add New Vehicle'}</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Name *</Label>
                <Input
                  placeholder="e.g., My Truck"
                  value={vehicleForm.name}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Make</Label>
                <Input
                  placeholder="Ford"
                  value={vehicleForm.make}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, make: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Model</Label>
                <Input
                  placeholder="F-150"
                  value={vehicleForm.model}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Year</Label>
                <Input
                  type="number"
                  placeholder="2024"
                  value={vehicleForm.year}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Current Odometer</Label>
                <Input
                  type="number"
                  placeholder="45000"
                  value={vehicleForm.initial_odometer}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, initial_odometer: e.target.value })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVehicleDialog(false); setEditingVehicle(null); }}>
              {editingVehicle ? 'Cancel' : 'Close'}
            </Button>
            <Button onClick={handleSaveVehicle} disabled={savingVehicle}>
              {savingVehicle ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingVehicle ? 'Save Changes' : 'Add Vehicle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={showManualEntryDialog} onOpenChange={setShowManualEntryDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Manual Trip</DialogTitle>
            <DialogDescription>Record a trip that wasn't tracked through the routing system.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={manualEntry.date}
                onChange={(e) => setManualEntry({ ...manualEntry, date: e.target.value })}
              />
            </div>
            
            <div>
              <Label>From Address *</Label>
              <Input
                placeholder="123 Main St, Austin, TX"
                value={manualEntry.from_address}
                onChange={(e) => setManualEntry({ ...manualEntry, from_address: e.target.value })}
              />
            </div>
            
            <div>
              <Label>To Address *</Label>
              <Input
                placeholder="456 Oak Ave, Austin, TX"
                value={manualEntry.to_address}
                onChange={(e) => setManualEntry({ ...manualEntry, to_address: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Miles (leave blank to calculate automatically)</Label>
              <Input
                type="number"
                step="0.1"
                placeholder="Auto-calculated via Mapbox"
                value={manualEntry.miles}
                onChange={(e) => setManualEntry({ ...manualEntry, miles: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Classification</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  variant={manualEntry.is_business === true ? "default" : "outline"}
                  size="sm"
                  className={manualEntry.is_business === true ? 'bg-green-500 hover:bg-green-600' : ''}
                  onClick={() => setManualEntry({ ...manualEntry, is_business: manualEntry.is_business === true ? null : true })}
                >
                  <Briefcase className="h-4 w-4 mr-1" /> Business
                </Button>
                <Button
                  type="button"
                  variant={manualEntry.is_business === false ? "default" : "outline"}
                  size="sm"
                  className={manualEntry.is_business === false ? 'bg-amber-500 hover:bg-amber-600' : ''}
                  onClick={() => setManualEntry({ ...manualEntry, is_business: manualEntry.is_business === false ? null : false })}
                >
                  <Coffee className="h-4 w-4 mr-1" /> Personal
                </Button>
              </div>
            </div>
            
            {vehicles.length > 0 && (
              <div>
                <Label>Vehicle</Label>
                <Select 
                  value={manualEntry.vehicle_id || 'none'} 
                  onValueChange={(v) => setManualEntry({ ...manualEntry, vehicle_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vehicle</SelectItem>
                    {vehicles.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name} {v.id === defaultVehicleId && '(Default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Purpose of trip..."
                value={manualEntry.notes}
                onChange={(e) => setManualEntry({ ...manualEntry, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualEntryDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateManualEntry} disabled={creatingManualEntry}>
              {creatingManualEntry ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
